import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReportIcon from "@mui/icons-material/Report";
import CloseIcon from "@mui/icons-material/Close";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import {
  fetchAwaitingApprovalComments,
  approveComment,
  spamComment,
  deleteComment,
  AwaitingApprovalComment,
} from "../pages/stream-management/components/memorial-page/comments-compiler/service/memoryBook.api";
import { tokens } from "../theme";
import {
  showErrorToast,
  showSuccessToast,
} from "../components/common/ShowToast";
import { getStatusColors } from "../constants/statusColors";

const CLAMP_LINES = 3;

function CommentContent({
  content,
  colors,
}: {
  content: string;
  colors: ReturnType<typeof tokens>;
}) {
  const [expanded, setExpanded] = useState(false);
  const isLong = content.length > 180;

  return (
    <Box>
      <Typography
        variant="body2"
        color={colors.primary[100]}
        sx={
          !expanded && isLong
            ? {
                mt: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: CLAMP_LINES,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : { mt: 0.5 }
        }
      >
        {content}
      </Typography>
      {isLong && (
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            p: 0,
            minWidth: 0,
            fontSize: "0.7rem",
            fontWeight: 700,
            color: colors.primary[400],
            mt: 0.25,
          }}
        >
          {expanded ? "Read less" : "Read more"}
        </Button>
      )}
    </Box>
  );
}

interface CommentsIconProps {
  iconButtonSx?: SxProps<Theme>;
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AwaitingApprovalComment[];
}

export default function CommentsIcon({ iconButtonSx }: CommentsIconProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const statusColors = getStatusColors(theme.palette.mode);

  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState<AwaitingApprovalComment[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await fetchAwaitingApprovalComments(1);
      setTotalCount(res.data?.count ?? 0);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    setComments([]);
    setNextPageUrl(null);
    try {
      const res = await fetchAwaitingApprovalComments(1);
      const data = res.data as PaginatedResponse;
      setComments(data?.results ?? []);
      setTotalCount(data?.count ?? 0);
      setNextPageUrl(data?.next ?? null);
    } catch {
      showErrorToast("Failed to load pending comments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchComments();
  }, [open, fetchComments]);

  const loadMore = async () => {
    if (!nextPageUrl) return;
    setLoadingMore(true);
    try {
      const urlPage = new URL(nextPageUrl).searchParams.get("page");
      const res = await fetchAwaitingApprovalComments(Number(urlPage) || 2);
      const data = res.data as PaginatedResponse;
      setComments((prev) => [...prev, ...(data?.results ?? [])]);
      setNextPageUrl(data?.next ?? null);
      setTotalCount(data?.count ?? 0);
    } catch {
      showErrorToast("Failed to load more comments");
    } finally {
      setLoadingMore(false);
    }
  };

  const removeFromList = (commentId: number) => {
    setComments((prev) => prev.filter((c) => c.id !== commentId));
    setTotalCount((t) => Math.max(0, t - 1));
  };

  const handleApprove = async (id: number) => {
    setBusyId(id);
    try {
      await approveComment(id);
      removeFromList(id);
      showSuccessToast("Comment approved");
    } catch {
      showErrorToast("Failed to approve comment");
    } finally {
      setBusyId(null);
    }
  };

  const handleSpam = async (id: number) => {
    setBusyId(id);
    try {
      await spamComment(id);
      removeFromList(id);
      showSuccessToast("Comment marked as spam");
    } catch {
      showErrorToast("Failed to mark comment as spam");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: number) => {
    setBusyId(id);
    try {
      await deleteComment(id);
      removeFromList(id);
      showSuccessToast("Comment deleted");
    } catch {
      showErrorToast("Failed to delete comment");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Tooltip title="Comments Pending Approval" arrow>
        <IconButton
          onClick={() => setOpen(true)}
          aria-label="Comments Pending Approval"
          sx={iconButtonSx}
        >
          <Badge badgeContent={totalCount || undefined} color="error">
            <ChatBubbleOutlineIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "90%", sm: 440 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" fontWeight={700} fontSize={22}>
                Pending Comments
              </Typography>
              {totalCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {totalCount} awaiting approval
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mt: 2 }} />
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={28} />
            </Box>
          ) : comments.length === 0 ? (
            <Box textAlign="center" py={8} color="text.secondary">
              <ChatBubbleOutlineIcon
                sx={{ fontSize: 44, opacity: 0.25, mb: 1 }}
              />
              <Typography variant="body2">
                No comments awaiting approval.
              </Typography>
            </Box>
          ) : (
            <AnimatePresence mode="popLayout">
              {comments.map((comment) => {
                const isBusy = busyId === comment.id;

                return (
                  <motion.div
                    key={comment.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 40, transition: { duration: 0.18 } }}
                    transition={{ duration: 0.15 }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        mb: 1.5,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? colors.primary[900]
                            : colors.primary[800],
                        borderLeft: `3px solid ${colors.primary[400]}`,
                        opacity: isBusy ? 0.5 : 1,
                        transition: "opacity 0.2s",
                      }}
                    >
                      {comment.subject_title && (
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: colors.primary[700],
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          {comment.subject_title}
                        </Typography>
                      )}

                      <CommentContent
                        content={comment.content}
                        colors={colors}
                      />

                      {(comment.stream_id || comment.booking_id) && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.75,
                            flexWrap: "wrap",
                            mt: 1,
                          }}
                        >
                          {comment.stream_id && (
                            <Chip
                              component="a"
                              href={`/stream-management/view/${comment.stream_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              size="small"
                              label="View Stream"
                              sx={{
                                bgcolor: statusColors.info.bg,
                                color: statusColors.info.fg,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                            />
                          )}
                          {comment.booking_id && (
                            <Chip
                              component="a"
                              href={`/bookings/${comment.booking_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              size="small"
                              label="View Booking"
                              sx={{
                                bgcolor: statusColors.success.bg,
                                color: statusColors.success.fg,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                            />
                          )}
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mt: 1,
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color={colors.primary[400]}
                          noWrap
                          sx={{ flexShrink: 1, minWidth: 0 }}
                        >
                          {comment.sender_full_name}
                        </Typography>

                        {isBusy ? (
                          <CircularProgress size={16} sx={{ flexShrink: 0 }} />
                        ) : (
                          <Box
                            sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}
                          >
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApprove(comment.id)}
                                sx={{ p: 0.5 }}
                              >
                                <CheckCircleOutlineIcon
                                  sx={{ fontSize: "1rem" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark as spam">
                              <IconButton
                                size="small"
                                onClick={() => handleSpam(comment.id)}
                                sx={{ p: 0.5, color: "#F57C00" }}
                              >
                                <ReportIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(comment.id)}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}

          {nextPageUrl && !loading && (
            <Box textAlign="center" mt={2} mb={2}>
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outlined"
                size="small"
              >
                {loadingMore ? <CircularProgress size={16} /> : "Load More"}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
}
